import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, CheckSquare, Square, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { createCentroCosto, updateCentroCosto } from './services/centroCostos.services';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';
const CATEGORIAS = ['Nómina', 'Arriendo', 'Servicios Públicos', 'Mantenimiento', 'Administrativo', 'Otro'];

const formatCurrency = (v) =>
  v != null && !isNaN(v)
    ? `$${Number(v).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : '—';

const fetchCentrosActivos = async () => {
  const res = await fetch(`${API_URL}/laundry/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 200, offset: 1, query: { estado: 'Activo' } }),
  });
  if (!res.ok) throw new Error('Error al cargar centros');
  const json = await res.json();
  return json.data || [];
};

const fetchClientesDeCentro = async (centroId) => {
  const res = await fetch(`${API_URL}/client/filter`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit: 500, offset: 1, query: { estado: 'Activo', 'lavanderia.id': centroId } }),
  });
  if (!res.ok) throw new Error('Error al cargar clientes');
  const json = await res.json();
  return json.data || [];
};

const CheckboxList = ({ items, selected, onToggle, onSelectAll, onSelectNone, loadingIds = {}, countLabel }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <p className="text-sm font-medium">{countLabel(Object.values(selected).filter(Boolean).length)}</p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onSelectAll}>Todos</Button>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onSelectNone}>Ninguno</Button>
      </div>
    </div>
    <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
      {items.map(item => (
        <div
          key={item._id}
          onClick={() => onToggle(item._id)}
          className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-muted/50 transition-colors"
        >
          {selected[item._id]
            ? <CheckSquare className="h-4 w-4 text-primary flex-shrink-0" />
            : <Square className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
          <span className="text-sm">{item.nombre}</span>
          {loadingIds[item._id] && <Loader2 className="h-3 w-3 animate-spin ml-auto text-muted-foreground" />}
        </div>
      ))}
    </div>
  </div>
);

const EMPTY_FORM = { descripcion: '', categoria: '', nivel: '', fecha: '', costoGeneral: '' };

// periodoActivo y periodoData vienen de la página padre
const CostoFormModal = ({ isOpen, onClose, onSaved, item, mode, periodoActivo, periodoData }) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  const [centros, setCentros] = useState([]);
  const [loadingCentros, setLoadingCentros] = useState(false);
  const [centrosSeleccionados, setCentrosSeleccionados] = useState({});
  const [clientesPorCentro, setClientesPorCentro] = useState({});
  const [loadingClientes, setLoadingClientes] = useState({});

  const [centroUnico, setCentroUnico] = useState('');
  const [clientesCentroSeleccionados, setClientesCentroSeleccionados] = useState({});

  const [centroParaCliente, setCentroParaCliente] = useState('');
  const [clientesDelCentroCliente, setClientesDelCentroCliente] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [loadingClienteUnico, setLoadingClienteUnico] = useState(false);

  // Método de distribución (por participación por defecto)
  const [metodoDistribucion, setMetodoDistribucion] = useState('participacion');

  // Display formateado del costo
  const [costoDisplay, setCostoDisplay] = useState('');

  const editCentrosInit = useRef(false);
  const editClientesCentroInit = useRef(false);
  const editClienteUnicoInit = useRef(false);

  // ── Reset al abrir/cerrar ────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    editCentrosInit.current = false;
    editClientesCentroInit.current = false;
    editClienteUnicoInit.current = false;

    if (mode === 'edit' && item) {
      setForm({
        descripcion: item.descripcion || '',
        categoria: item.categoria || '',
        nivel: item.nivel || '',
        fecha: item.fecha ? item.fecha.substring(0, 10) : '',
        costoGeneral: item.costoGeneral ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setCentros([]);
    setCentrosSeleccionados({});
    setClientesPorCentro({});
    setCentroUnico('');
    setClientesCentroSeleccionados({});
    setCentroParaCliente('');
    setClienteSeleccionado('');
    setClientesDelCentroCliente([]);
    setMetodoDistribucion('participacion');
    // Inicializar display formateado del costo
    const costoInicial = mode === 'edit' && item?.costoGeneral ? item.costoGeneral : '';
    setCostoDisplay(costoInicial !== '' ? Number(costoInicial).toLocaleString('es-CO') : '');
  }, [isOpen, mode, item]);

  // ── Carga de centros ─────────────────────────────────────────────────────
  const loadCentros = useCallback(async () => {
    if (centros.length > 0) return;
    setLoadingCentros(true);
    try {
      const data = await fetchCentrosActivos();
      setCentros(data);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingCentros(false);
    }
  }, [centros.length, toast]);

  useEffect(() => {
    if (!isOpen || !form.nivel) return;
    loadCentros();
  }, [form.nivel, isOpen, loadCentros]);

  // ── Inicializar centrosSeleccionados ─────────────────────────────────────
  useEffect(() => {
    if (!isOpen || centros.length === 0 || form.nivel !== 'empresa') return;
    if (mode === 'edit' && item?.centros?.length > 0 && !editCentrosInit.current) {
      editCentrosInit.current = true;
      const fromItem = {};
      centros.forEach(c => { fromItem[c._id] = item.centros.some(ic => ic.id === c._id); });
      setCentrosSeleccionados(fromItem);
    } else if (mode === 'create' && Object.keys(centrosSeleccionados).length === 0) {
      const all = {};
      centros.forEach(c => { all[c._id] = true; });
      setCentrosSeleccionados(all);
    }
  }, [centros, isOpen, form.nivel, mode, item, centrosSeleccionados]);

  useEffect(() => {
    if (!isOpen || centros.length === 0 || form.nivel !== 'centro' || mode !== 'edit' || !item) return;
    if (editCentrosInit.current) return;
    editCentrosInit.current = true;
    setCentroUnico(item.centros?.[0]?.id || '');
  }, [centros, isOpen, form.nivel, mode, item]);

  useEffect(() => {
    if (!isOpen || centros.length === 0 || form.nivel !== 'cliente' || mode !== 'edit' || !item) return;
    if (editCentrosInit.current) return;
    editCentrosInit.current = true;
    setCentroParaCliente(item.centros?.[0]?.id || '');
  }, [centros, isOpen, form.nivel, mode, item]);

  // ── Carga de clientes por centro (cache) ─────────────────────────────────
  const loadClientesDeCentroCache = useCallback(async (centroId) => {
    if (clientesPorCentro[centroId] !== undefined) return;
    setLoadingClientes(prev => ({ ...prev, [centroId]: true }));
    try {
      const data = await fetchClientesDeCentro(centroId);
      setClientesPorCentro(prev => ({ ...prev, [centroId]: data }));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoadingClientes(prev => ({ ...prev, [centroId]: false }));
    }
  }, [clientesPorCentro, toast]);

  useEffect(() => {
    if (form.nivel !== 'empresa') return;
    Object.entries(centrosSeleccionados).forEach(([id, selected]) => {
      if (selected) loadClientesDeCentroCache(id);
    });
  }, [centrosSeleccionados, form.nivel, loadClientesDeCentroCache]);

  useEffect(() => {
    if (form.nivel !== 'centro' || !centroUnico) return;
    loadClientesDeCentroCache(centroUnico);
  }, [centroUnico, form.nivel, loadClientesDeCentroCache]);

  useEffect(() => {
    if (form.nivel !== 'centro' || !centroUnico) return;
    const clientes = clientesPorCentro[centroUnico];
    if (!clientes || clientes.length === 0) return;
    setClientesCentroSeleccionados(prev => {
      const hasAny = clientes.some(c => prev[c._id] !== undefined);
      if (hasAny) return prev;
      const init = {};
      if (mode === 'edit' && item?.clientes?.length > 0 && !editClientesCentroInit.current) {
        editClientesCentroInit.current = true;
        clientes.forEach(c => { init[c._id] = item.clientes.some(ic => ic.id === c._id); });
      } else {
        clientes.forEach(c => { init[c._id] = true; });
      }
      return init;
    });
  }, [clientesPorCentro, centroUnico, form.nivel, mode, item]);

  useEffect(() => {
    if (form.nivel !== 'cliente' || !centroParaCliente) return;
    setLoadingClienteUnico(true);
    fetchClientesDeCentro(centroParaCliente)
      .then(data => setClientesDelCentroCliente(data))
      .catch(err => toast({ title: 'Error', description: err.message, variant: 'destructive' }))
      .finally(() => setLoadingClienteUnico(false));
  }, [centroParaCliente, form.nivel, toast]);

  useEffect(() => {
    if (form.nivel !== 'cliente' || mode !== 'edit' || !item || editClienteUnicoInit.current) return;
    if (clientesDelCentroCliente.length === 0) return;
    editClienteUnicoInit.current = true;
    setClienteSeleccionado(item.clientes?.[0]?.id || '');
  }, [clientesDelCentroCliente, form.nivel, mode, item]);

  // ── Cambio de nivel ──────────────────────────────────────────────────────
  const handleNivelChange = (v) => {
    setForm(f => ({ ...f, nivel: v }));
    setCentroUnico('');
    setClientesCentroSeleccionados({});
    setCentroParaCliente('');
    setClienteSeleccionado('');
    setClientesDelCentroCliente([]);
    setMetodoDistribucion('equitativo');
    editCentrosInit.current = true;
    if (v === 'empresa' && centros.length > 0) {
      const all = {};
      centros.forEach(c => { all[c._id] = true; });
      setCentrosSeleccionados(all);
    }
  };

  // ── Distribución con participación guardada ──────────────────────────────
  const calcularDistribucionParticipacion = (costo) => {
    if (!periodoData?.clientes?.length) return null;

    if (form.nivel === 'empresa') {
      // Usar todos los clientes del período con su % general
      const clientesFlat = periodoData.clientes
        .filter(c => c.participacionGeneral > 0 || periodoData.totalVentas === 0)
        .map(c => {
          const pct = periodoData.totalVentas > 0 ? c.participacionGeneral / 100 : 1 / periodoData.clientes.length;
          return { id: c.id, nombre: c.nombre, costo: costo * pct, centroId: c.centroId, participacion: pct };
        });

      const centroIds = [...new Set(clientesFlat.map(c => c.centroId).filter(Boolean))];
      const centrosFlat = centroIds.map(id => {
        const info = periodoData.clientes.find(c => c.centroId === id);
        const costoCentro = clientesFlat.filter(cl => cl.centroId === id).reduce((s, cl) => s + cl.costo, 0);
        return { id, nombre: info?.centroNombre || id, costo: costoCentro };
      });

      return {
        centros: centrosFlat, numeroCentros: centrosFlat.length,
        costoPorCentro: centrosFlat.length > 0 ? costo / centrosFlat.length : 0,
        clientes: clientesFlat, numeroClientes: clientesFlat.length,
        costoPorCliente: clientesFlat.length > 0 ? costo / clientesFlat.length : 0,
        metodo: 'participacion',
      };
    }

    if (form.nivel === 'centro' && centroUnico) {
      const centroData = centros.find(x => x._id === centroUnico);
      // Clientes del período que pertenecen a este centro
      const clientesDeCentro = periodoData.clientes.filter(c => c.centroId === centroUnico);
      if (!clientesDeCentro.length) return null;

      const totalCentro = clientesDeCentro.reduce((s, c) => s + c.ventas, 0);
      const clientesFlat = clientesDeCentro.map(c => {
        const pct = totalCentro > 0 ? c.ventas / totalCentro : 1 / clientesDeCentro.length;
        return { id: c.id, nombre: c.nombre, costo: costo * pct, participacion: pct };
      });

      return {
        centros: [{ id: centroUnico, nombre: centroData?.nombre || centroUnico, costo }],
        numeroCentros: 1, costoPorCentro: costo,
        clientes: clientesFlat, numeroClientes: clientesFlat.length,
        costoPorCliente: clientesFlat.length > 0 ? costo / clientesFlat.length : 0,
        metodo: 'participacion',
      };
    }

    return null;
  };

  // ── Distribución equitativa ──────────────────────────────────────────────
  const calcularDistribucionEquitativa = (costo) => {
    if (form.nivel === 'empresa') {
      const selectedIds = Object.entries(centrosSeleccionados).filter(([, v]) => v).map(([id]) => id);
      if (!selectedIds.length) return null;
      const costoPorCentro = costo / selectedIds.length;
      const centrosFlat = selectedIds.map(id => {
        const c = centros.find(x => x._id === id);
        return { id, nombre: c?.nombre || id, costo: costoPorCentro };
      });
      const clientesFlat = selectedIds.flatMap(id => {
        const lista = clientesPorCentro[id] || [];
        const costoCli = lista.length > 0 ? costoPorCentro / lista.length : 0;
        return lista.map(c => ({ id: c._id, nombre: c.nombre, costo: costoCli }));
      });
      return {
        centros: centrosFlat, numeroCentros: centrosFlat.length, costoPorCentro,
        clientes: clientesFlat, numeroClientes: clientesFlat.length,
        costoPorCliente: clientesFlat.length > 0 ? costo / clientesFlat.length : 0,
      };
    }

    if (form.nivel === 'centro' && centroUnico) {
      const c = centros.find(x => x._id === centroUnico);
      const todosClientes = clientesPorCentro[centroUnico] || [];
      const activos = todosClientes.filter(x => clientesCentroSeleccionados[x._id]);
      const costoPorCliente = activos.length > 0 ? costo / activos.length : 0;
      return {
        centros: [{ id: centroUnico, nombre: c?.nombre || centroUnico, costo }],
        numeroCentros: 1, costoPorCentro: costo,
        clientes: activos.map(x => ({ id: x._id, nombre: x.nombre, costo: costoPorCliente })),
        numeroClientes: activos.length, costoPorCliente,
      };
    }

    return null;
  };

  const calcularDistribucion = () => {
    const costo = parseFloat(form.costoGeneral) || 0;
    if (!costo) return null;
    if (metodoDistribucion === 'participacion') return calcularDistribucionParticipacion(costo);
    return calcularDistribucionEquitativa(costo);
  };

  const distribucion = (form.nivel === 'empresa' || form.nivel === 'centro') ? calcularDistribucion() : null;

  // ── Guardar ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.descripcion || !form.categoria || !form.nivel || !form.fecha || !form.costoGeneral) {
      toast({ title: 'Campos requeridos', description: 'Completa todos los campos obligatorios', variant: 'destructive' });
      return;
    }
    const costo = parseFloat(form.costoGeneral);
    if (isNaN(costo) || costo <= 0) {
      toast({ title: 'Costo inválido', description: 'El costo debe ser mayor a 0', variant: 'destructive' });
      return;
    }

    let payload = {
      descripcion: form.descripcion, categoria: form.categoria, nivel: form.nivel,
      fecha: new Date(form.fecha), costoGeneral: costo,
      creadoPor: user ? { id: user.id || user._id || '', nombre: user.name || '' } : undefined,
    };

    if (form.nivel === 'empresa' || form.nivel === 'centro') {
      if (!distribucion) {
        toast({ title: 'Sin distribución', description: metodoDistribucion === 'participacion' ? 'No hay ventas registradas para este período' : 'Selecciona al menos un centro', variant: 'destructive' });
        return;
      }
      const clientesLimpios = distribucion.clientes.map(({ id, nombre, costo: c }) => ({ id, nombre, costo: c }));
      payload = { ...payload, ...distribucion, clientes: clientesLimpios };
    }

    if (form.nivel === 'cliente') {
      if (!clienteSeleccionado) {
        toast({ title: 'Selecciona un cliente', variant: 'destructive' });
        return;
      }
      const centroDatos = centros.find(c => c._id === centroParaCliente);
      const clienteDatos = clientesDelCentroCliente.find(c => c._id === clienteSeleccionado);
      payload.centros = [{ id: centroParaCliente, nombre: centroDatos?.nombre || '', costo }];
      payload.numeroCentros = 1;
      payload.costoPorCentro = costo;
      payload.clientes = [{ id: clienteSeleccionado, nombre: clienteDatos?.nombre || '', costo }];
      payload.numeroClientes = 1;
      payload.costoPorCliente = costo;
    }

    setIsSaving(true);
    try {
      if (mode === 'edit' && item?._id) {
        await updateCentroCosto(item._id, payload);
        toast({ title: 'Costo actualizado' });
      } else {
        await createCentroCosto(payload);
        toast({ title: 'Costo registrado' });
      }
      onSaved();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Handlers del input de costo formateado ──────────────────────────────
  const handleCostoChange = (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, '');
    setCostoDisplay(digits);
    setForm(f => ({ ...f, costoGeneral: digits }));
  };
  const handleCostoFocus = () => {
    setCostoDisplay(form.costoGeneral ? String(form.costoGeneral) : '');
  };
  const handleCostoBlur = () => {
    const val = form.costoGeneral;
    setCostoDisplay(val ? Number(val).toLocaleString('es-CO') : '');
  };

  // ── Límites de fecha según el período activo ─────────────────────────────
  const fechaMin = periodoActivo
    ? `${periodoActivo}-01`
    : undefined;
  const fechaMax = periodoActivo
    ? (() => {
        const [y, m] = periodoActivo.split('-').map(Number);
        const last = new Date(y, m, 0).getDate();
        return `${periodoActivo}-${String(last).padStart(2, '0')}`;
      })()
    : undefined;

  const clientesCentroActuales = centroUnico ? (clientesPorCentro[centroUnico] || []) : [];
  const puedeUsarParticipacion = (form.nivel === 'empresa' || form.nivel === 'centro') && !!periodoData;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Editar Costo' : 'Registrar Nuevo Gasto'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Campos base */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1">
              <Label>Descripción <span className="text-red-500">*</span></Label>
              <Input placeholder="Ej: Salario contador enero" value={form.descripcion}
                onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Categoría <span className="text-red-500">*</span></Label>
              <Select value={form.categoria} onValueChange={v => setForm(f => ({ ...f, categoria: v }))}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{CATEGORIAS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Nivel <span className="text-red-500">*</span></Label>
              <Select value={form.nivel} onValueChange={handleNivelChange}>
                <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="empresa">Empresa (todos los centros)</SelectItem>
                  <SelectItem value="centro">Centro de Lavado</SelectItem>
                  <SelectItem value="cliente">Cliente específico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Fecha <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={form.fecha}
                min={fechaMin}
                max={fechaMax}
                onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Costo Total (COP) <span className="text-red-500">*</span></Label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="0"
                value={costoDisplay}
                onChange={handleCostoChange}
                onFocus={handleCostoFocus}
                onBlur={handleCostoBlur}
              />
            </div>
          </div>

          {/* ── SELECTOR DE MÉTODO (empresa y centro) ── */}
          {(form.nivel === 'empresa' || form.nivel === 'centro') && (
            <div className="space-y-2">
              <Label className="text-sm">Método de distribución</Label>
              <div className="flex gap-2">
                <Button
                  type="button" size="sm"
                  variant={metodoDistribucion === 'equitativo' ? 'default' : 'outline'}
                  className="h-8 text-xs"
                  onClick={() => setMetodoDistribucion('equitativo')}
                >
                  Equitativo
                </Button>
                <Button
                  type="button" size="sm"
                  variant={metodoDistribucion === 'participacion' ? 'default' : 'outline'}
                  className="h-8 text-xs"
                  disabled={!puedeUsarParticipacion}
                  onClick={() => setMetodoDistribucion('participacion')}
                  title={!periodoData ? 'Primero registra las ventas del período en el Paso 1' : undefined}
                >
                  Por participación de ventas
                </Button>
              </div>
              {!periodoData && (form.nivel === 'empresa' || form.nivel === 'centro') && (
                <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Para distribuir por participación, registra primero las ventas del período en el Paso 1.
                </p>
              )}
              {periodoData && metodoDistribucion === 'participacion' && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  Usando ventas de <strong>{periodoActivo}</strong> · {periodoData.clientes?.length} clientes · {formatCurrency(periodoData.totalVentas)} en ventas totales
                </p>
              )}
            </div>
          )}

          {/* ── NIVEL EMPRESA (equitativo) ── */}
          {form.nivel === 'empresa' && metodoDistribucion === 'equitativo' && (
            <div className="border border-border rounded-lg p-4 bg-muted/20">
              {loadingCentros ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Cargando centros...
                </div>
              ) : (
                <CheckboxList
                  items={centros}
                  selected={centrosSeleccionados}
                  onToggle={id => setCentrosSeleccionados(prev => ({ ...prev, [id]: !prev[id] }))}
                  onSelectAll={() => { const a = {}; centros.forEach(c => { a[c._id] = true; }); setCentrosSeleccionados(a); }}
                  onSelectNone={() => { const a = {}; centros.forEach(c => { a[c._id] = false; }); setCentrosSeleccionados(a); }}
                  loadingIds={loadingClientes}
                  countLabel={n => `Centros activos (${n} de ${centros.length} seleccionados)`}
                />
              )}
            </div>
          )}

          {/* ── NIVEL EMPRESA (participación) — sin selección manual de centros ── */}
          {form.nivel === 'empresa' && metodoDistribucion === 'participacion' && periodoData && (
            <div className="border border-blue-200 dark:border-blue-800 rounded-lg p-4 bg-blue-500/5 text-sm">
              <p className="font-medium text-blue-700 dark:text-blue-300 mb-1">Distribución automática por participación</p>
              <p className="text-xs text-muted-foreground">
                El gasto se distribuirá entre todos los {periodoData.clientes?.length} clientes del período según su porcentaje de participación general en ventas.
              </p>
            </div>
          )}

          {/* ── NIVEL CENTRO ── */}
          {form.nivel === 'centro' && (
            <div className="space-y-3 border border-border rounded-lg p-4 bg-muted/20">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Centro de Lavado <span className="text-red-500">*</span></Label>
                {loadingCentros ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
                  </div>
                ) : (
                  <Select value={centroUnico} onValueChange={v => { setCentroUnico(v); setClientesCentroSeleccionados({}); }}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar centro..." /></SelectTrigger>
                    <SelectContent>{centros.map(c => <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>

              {/* Equitativo: checkbox de clientes */}
              {centroUnico && metodoDistribucion === 'equitativo' && (
                loadingClientes[centroUnico] ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando clientes...
                  </div>
                ) : clientesCentroActuales.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Este centro no tiene clientes activos</p>
                ) : (
                  <CheckboxList
                    items={clientesCentroActuales}
                    selected={clientesCentroSeleccionados}
                    onToggle={id => setClientesCentroSeleccionados(prev => ({ ...prev, [id]: !prev[id] }))}
                    onSelectAll={() => { const a = {}; clientesCentroActuales.forEach(c => { a[c._id] = true; }); setClientesCentroSeleccionados(a); }}
                    onSelectNone={() => { const a = {}; clientesCentroActuales.forEach(c => { a[c._id] = false; }); setClientesCentroSeleccionados(a); }}
                    countLabel={n => `Clientes (${n} de ${clientesCentroActuales.length} seleccionados)`}
                  />
                )
              )}

              {/* Participación: info automática */}
              {centroUnico && metodoDistribucion === 'participacion' && periodoData && (() => {
                const clientesDeCentro = periodoData.clientes?.filter(c => c.centroId === centroUnico) || [];
                return clientesDeCentro.length > 0 ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400">
                    {clientesDeCentro.length} clientes con ventas registradas en este centro para el período {periodoActivo}.
                  </p>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Este centro no tiene ventas registradas en el período {periodoActivo}. Se usará reparto equitativo.
                  </p>
                );
              })()}
            </div>
          )}

          {/* ── NIVEL CLIENTE ── */}
          {form.nivel === 'cliente' && (
            <div className="space-y-3 border border-border rounded-lg p-4 bg-muted/20">
              <div className="space-y-1">
                <Label className="text-sm font-medium">Centro de Lavado <span className="text-red-500">*</span></Label>
                {loadingCentros ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando centros...
                  </div>
                ) : (
                  <Select value={centroParaCliente} onValueChange={v => { setCentroParaCliente(v); setClienteSeleccionado(''); }}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar centro..." /></SelectTrigger>
                    <SelectContent>{centros.map(c => <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              {centroParaCliente && (
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Cliente <span className="text-red-500">*</span></Label>
                  {loadingClienteUnico ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" /> Cargando clientes...
                    </div>
                  ) : (
                    <Select value={clienteSeleccionado} onValueChange={setClienteSeleccionado}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar cliente..." /></SelectTrigger>
                      <SelectContent>
                        {clientesDelCentroCliente.map(c => <SelectItem key={c._id} value={c._id}>{c.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── PREVISUALIZACIÓN ── */}
          {distribucion && distribucion.centros.length > 0 && parseFloat(form.costoGeneral) > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 border-b border-border flex items-center justify-between">
                <p className="text-sm font-medium">Vista previa de distribución</p>
                <span className="text-xs text-muted-foreground">
                  {distribucion.numeroCentros} centro(s) · {distribucion.numeroClientes} cliente(s)
                  {distribucion.metodo === 'participacion' && ' · por participación'}
                </span>
              </div>
              <div className="overflow-x-auto max-h-52">
                <table className="w-full text-xs">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Centro</th>
                      <th className="px-3 py-1.5 text-right font-medium text-muted-foreground">Costo Centro</th>
                      <th className="px-3 py-1.5 text-left font-medium text-muted-foreground pl-4 border-l border-border">Cliente</th>
                      {distribucion.metodo === 'participacion' && (
                        <th className="px-3 py-1.5 text-right font-medium text-muted-foreground">Participación</th>
                      )}
                      <th className="px-3 py-1.5 text-right font-medium text-muted-foreground">Costo Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {distribucion.centros.map(centro => {
                      const clientesDeCentro = distribucion.clientes.filter(cl =>
                        cl.centroId ? cl.centroId === centro.id : true
                      );
                      return clientesDeCentro.length > 0 ? (
                        clientesDeCentro.map((cl, i) => (
                          <tr key={`${centro.id}-${cl.id}`} className="border-t border-border">
                            {i === 0 && (
                              <td className="px-3 py-1.5 font-medium" rowSpan={clientesDeCentro.length}>{centro.nombre}</td>
                            )}
                            {i === 0 && (
                              <td className="px-3 py-1.5 text-right tabular-nums" rowSpan={clientesDeCentro.length}>{formatCurrency(centro.costo)}</td>
                            )}
                            <td className="px-3 py-1.5 pl-4 border-l border-border text-muted-foreground">{cl.nombre}</td>
                            {distribucion.metodo === 'participacion' && (
                              <td className="px-3 py-1.5 text-right tabular-nums text-blue-600 dark:text-blue-400">
                                {cl.participacion != null ? `${(cl.participacion * 100).toFixed(2)}%` : '—'}
                              </td>
                            )}
                            <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(cl.costo)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr key={centro.id} className="border-t border-border">
                          <td className="px-3 py-1.5 font-medium">{centro.nombre}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(centro.costo)}</td>
                          <td className="px-3 py-1.5 pl-4 border-l border-border text-muted-foreground italic" colSpan={distribucion.metodo === 'participacion' ? 3 : 2}>Sin clientes</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t-2 border-border bg-muted/20 font-medium">
                      <td className="px-3 py-1.5">Total</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(parseFloat(form.costoGeneral))}</td>
                      <td className="px-3 py-1.5 pl-4 border-l border-border text-muted-foreground text-xs" colSpan={distribucion.metodo === 'participacion' ? 2 : 1}>
                        {distribucion.metodo === 'participacion'
                          ? `${distribucion.numeroClientes} clientes por participación de ventas`
                          : `${distribucion.numeroClientes} clientes · ${formatCurrency(distribucion.costoPorCliente)} c/u`}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(parseFloat(form.costoGeneral))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {form.nivel === 'cliente' && clienteSeleccionado && parseFloat(form.costoGeneral) > 0 && (
            <div className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 bg-emerald-500/5 text-sm">
              <p className="font-medium text-emerald-700 dark:text-emerald-300">
                Costo asignado al cliente: {formatCurrency(parseFloat(form.costoGeneral))}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'edit' ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CostoFormModal;
